import { useState, useEffect } from 'react';
import { X, Search, Star, MapPin, Briefcase, Clock, AlertCircle, MessageSquare } from 'lucide-react';
import apiService from '@/services/api';

interface Provider {
  id: string;
  provider_id: number;
  user_id: number;
  name: string;
  company_name: string;
  email: string;
  phone: string;
  rating: number;
  reviewCount: number;
  services: string[];
  activeJobs: number;
  distance: string;
  availability: string;
  isReady: boolean;
}

interface AssignProviderModalProps {
  jobId: string;
  serviceType: string;
  location: string;
  scheduledTime: string;
  priority: 'High' | 'Medium' | 'Low';
  isOpen: boolean;
  onClose: () => void;
  onAssign: (providerId: string, notes: string, notifications: { app: boolean; sms: boolean }) => void;
}

const AssignProviderModal = ({
  jobId,
  serviceType,
  location,
  scheduledTime,
  priority,
  isOpen,
  onClose,
  onAssign,
}: AssignProviderModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [notifyVia, setNotifyVia] = useState({ app: true, sms: false });
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAvailableProviders();
    }
  }, [isOpen]);

  const fetchAvailableProviders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get(`/admin/assign/available-providers?service_id=${serviceType}`);
      
      if (response?.success && Array.isArray(response.data)) {
        setProviders(response.data);
        if (response.data.length > 0) {
          setSelectedProviderId(response.data[0].id);
        }
      } else {
        setError('Failed to load providers');
        setProviders([]);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load available providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProviders = providers.filter((provider) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (provider.name || '').toLowerCase().includes(q) ||
      (provider.email || '').toLowerCase().includes(q) ||
      (provider.company_name || '').toLowerCase().includes(q) ||
      (provider.phone || '').toLowerCase().includes(q) ||
      provider.services.some((s) => (s || '').toLowerCase().includes(q)) ||
      (provider.distance || '').toLowerCase().includes(q)
    );
  });

  const handleAssign = () => {
    if (selectedProviderId) {
      onAssign(selectedProviderId, notes, notifyVia);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedProviderId(providers.length > 0 ? providers[0].id : null);
    setNotes('');
    setNotifyVia({ app: true, sms: false });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assign Provider</h2>
            <p className="text-sm text-gray-600 mt-1">JOB ID: {jobId}</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Job Details */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs uppercase font-bold text-gray-500 mb-1">Service Type</p>
              <p className="text-sm font-semibold text-gray-900">{serviceType}</p>
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-gray-500 mb-1">Location</p>
              <p className="text-sm font-semibold text-gray-900">{location}</p>
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-gray-500 mb-1">Scheduled Time</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">{scheduledTime}</p>
                <div className="flex items-center gap-1 px-2 py-1 bg-red-100 rounded">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  <span className="text-xs font-bold text-red-700">Priority: {priority}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Provider Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold uppercase text-gray-500 tracking-wider">Select Available Provider</p>
              <p className="text-xs font-medium text-gray-600">
                {loading ? 'Loading...' : `Showing ${filteredProviders.length} matched specialists`}
              </p>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, expertise, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={loading}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="p-4 rounded-lg border-2 border-gray-200 animate-pulse">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                        <div className="space-y-2">
                          <div className="w-24 h-4 bg-gray-300 rounded"></div>
                          <div className="w-32 h-3 bg-gray-300 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Provider Cards */}
            {!loading && filteredProviders.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {filteredProviders.map((provider) => (
                  <div
                    key={provider.id}
                    onClick={() => setSelectedProviderId(provider.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedProviderId === provider.id
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                          {provider.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 text-sm truncate">{provider.name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                            <span className="text-xs font-semibold text-gray-700 truncate">
                              {provider.rating} ({provider.reviewCount} Reviews)
                            </span>
                          </div>
                        </div>
                      </div>
                      {selectedProviderId === provider.id && (
                        <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white flex-shrink-0">
                          ✓
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 font-medium mb-1">Services</p>
                        <p className="text-gray-700 font-semibold line-clamp-2">
                          {provider.services.length > 0 ? provider.services.join(', ') : 'General'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium mb-1">Active Jobs</p>
                        <p className="text-gray-700 font-semibold">{provider.activeJobs} Today</p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium mb-1">Distance</p>
                        <p className="text-gray-700 font-semibold">{provider.distance}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium mb-1">Availability</p>
                        <div className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${provider.isReady ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <p className="text-gray-700 font-semibold">{provider.availability}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Providers Found */}
            {!loading && filteredProviders.length === 0 && providers.length > 0 && (
              <div className="p-4 text-center text-gray-500">
                <p>No providers match your search criteria</p>
              </div>
            )}

            {/* No Providers Available */}
            {!loading && providers.length === 0 && !error && (
              <div className="p-4 text-center text-gray-500">
                <p>No available providers at this time</p>
              </div>
            )}
          </div>

          {/* Internal Notes */}
          <div>
            <label className="block text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">
              Internal Assignment Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Mention specific tools or site access codes..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              rows={4}
            />
          </div>

          {/* Notifications */}
          <div>
            <label className="block text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">
              Notifications
            </label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Notify Provider via App</span>
                </div>
                <button
                  onClick={() => setNotifyVia({ ...notifyVia, app: !notifyVia.app })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    notifyVia.app ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      notifyVia.app ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Send SMS Backup</span>
                </div>
                <button
                  onClick={() => setNotifyVia({ ...notifyVia, sms: !notifyVia.sms })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    notifyVia.sms ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      notifyVia.sms ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-gray-700 font-semibold hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedProviderId || loading}
            className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Assign Provider'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignProviderModal;
